import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search,
  Pause,
  Play,
  Download,
  X,
  Filter,
  Copy,
} from 'lucide-react';
import { PageHeader, PageBody } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Tooltip } from '@/components/ui/Tooltip';
import { Term } from '@/components/ui/Term';
import { useLogs } from '@/api/logs';
import { useTenants } from '@/api/tenants';
import type { LogLevel, LogLine } from '@/api/types';
import { REGION_LABEL } from '@/lib/tier';
import { downloadCsv, timestampedFilename } from '@/lib/download';
import { cn } from '@/lib/cn';

const LEVEL_TONE: Record<LogLevel, 'neutral' | 'accent' | 'warn' | 'danger'> = {
  info: 'neutral',
  debug: 'neutral',
  warn: 'warn',
  error: 'danger',
};

const TIME_RANGES = [
  { key: '15m', label: 'Last 15 minutes' },
  { key: '1h', label: 'Last hour' },
  { key: '24h', label: 'Last 24 hours' },
  { key: '7d', label: 'Last 7 days' },
];

interface UILogLine {
  id: string;
  ts: string;
  level: LogLevel;
  tenantId: string;
  tenantName: string;
  workspaceId: string;
  workspaceName: string;
  region: string;
  source: string;
  msg: string;
}

function apiToUI(l: LogLine): UILogLine {
  return {
    id: l.id, ts: l.ts, level: l.level,
    tenantId: l.tenant_id, tenantName: l.tenant_name,
    workspaceId: l.workspace_id, workspaceName: l.workspace_name,
    region: l.region, source: l.source, msg: l.msg,
  };
}

export function LogsRoute() {
  const [params, setParams] = useSearchParams();

  // Read from URL params (shareable / bookmarkable filter state).
  const q = params.get('q') ?? '';
  const tenantId = params.get('tenant') ?? '';
  const workspaceId = params.get('workspace') ?? '';
  const source = params.get('source') ?? '';
  const region = params.get('region') ?? '';
  const range = params.get('range') ?? '1h';
  const live = params.get('live') === '1';
  const levelsParam = params.get('levels') ?? 'error,warn,info,debug';
  const levels = new Set<LogLevel>(levelsParam.split(',').filter(Boolean) as LogLevel[]);

  // Live tail simulation — bump a counter every 2s so the bottom log line "ages".
  const [, force] = useState(0);
  const liveTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (live) {
      liveTimer.current = setInterval(() => force(c => c + 1), 2000);
    }
    return () => {
      if (liveTimer.current) clearInterval(liveTimer.current);
    };
  }, [live]);

  function patch(next: Partial<Record<string, string>>) {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === undefined || v === '' || v === null) merged.delete(k);
      else merged.set(k, v);
    }
    setParams(merged, { replace: true });
  }

  function toggleLevel(l: LogLevel) {
    const next = new Set(levels);
    if (next.has(l)) next.delete(l);
    else next.add(l);
    patch({ levels: Array.from(next).join(',') });
  }

  function clearAll() {
    setParams({}, { replace: true });
  }

  // Pull from the API; fall back to the mock fixture when the backend
  // isn't reachable. Both feed the same UILogLine shape after adaptation.
  const { data: apiLogs = [] } = useLogs({
    tenant_id: tenantId || undefined,
    workspace_id: workspaceId || undefined,
    level: Array.from(levels),
    q: q || undefined,
    since: range,
  }, { live });
  const { data: apiTenants = [] } = useTenants();

  const allLogs: UILogLine[] = useMemo(() => apiLogs.map(apiToUI), [apiLogs]);

  const filtered = useMemo(() => {
    return allLogs.filter(line => {
      if (!levels.has(line.level)) return false;
      if (tenantId && line.tenantId !== tenantId) return false;
      if (workspaceId && line.workspaceId !== workspaceId) return false;
      if (source && line.source !== source) return false;
      if (region && line.region !== region) return false;
      if (q && !(`${line.msg} ${line.workspaceName} ${line.tenantName} ${line.source}`.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [q, tenantId, workspaceId, source, region, levels, allLogs]);

  const tenantList = apiTenants.map(t => ({ id: t.id, slug: t.slug, name: t.name }));
  const tenantLabel = tenantId ? tenantList.find(t => t.id === tenantId)?.name ?? tenantId : null;
  const workspaceLabel = workspaceId ? allLogs.find(l => l.workspaceId === workspaceId)?.workspaceName ?? workspaceId : null;

  const activeChips: { key: string; label: string; clear: () => void }[] = [];
  if (q) activeChips.push({ key: 'q', label: `Query: "${q}"`, clear: () => patch({ q: '' }) });
  if (tenantLabel) activeChips.push({ key: 'tenant', label: `Customer: ${tenantLabel}`, clear: () => patch({ tenant: '' }) });
  if (workspaceLabel) activeChips.push({ key: 'workspace', label: `Workspace: ${workspaceLabel}`, clear: () => patch({ workspace: '' }) });
  if (source) activeChips.push({ key: 'source', label: `Source: ${source}`, clear: () => patch({ source: '' }) });
  if (region) activeChips.push({ key: 'region', label: `Region: ${REGION_LABEL[region] ?? region}`, clear: () => patch({ region: '' }) });

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Logs"
        description={
          <>
            Searchable application logs from every customer{' '}
            <Term k="workspace">instance</Term> across all regions.
            Filters update the URL so links are shareable. Use the live-tail
            toggle to stream new entries in real time.
          </>
        }
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Download size={13} />}
              onClick={() =>
                downloadCsv(
                  [
                    ['timestamp', 'level', 'tenant', 'workspace', 'region', 'source', 'message'],
                    ...filtered.map(l => [l.ts, l.level, l.tenantName, l.workspaceName, l.region, l.source, l.msg]),
                  ],
                  timestampedFilename('nousviz-logs', 'csv'),
                )
              }
            >
              Export ({filtered.length})
            </Button>
            <Button variant="secondary" size="sm" iconLeft={<Copy size={13} />} onClick={() => navigator.clipboard?.writeText(window.location.href)}>
              Copy shareable URL
            </Button>
          </>
        }
      />
      <PageBody>
        <Card>
          {/* Filter bar */}
          <div className="px-3 py-2.5 border-b border-border flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-md min-w-[180px]">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none" />
              <input
                value={q}
                onChange={e => patch({ q: e.target.value })}
                placeholder="Filter log lines…"
                className="w-full h-7 pl-7 pr-2 rounded-md bg-surface-2 border border-transparent text-sm hover:border-border focus:border-accent focus:outline-none"
              />
            </div>

            <Select
              label="Customer"
              value={tenantId}
              onChange={v => patch({ tenant: v })}
              options={[
                ['', 'All'],
                ...tenantList.map(t => [t.id, t.name] as [string, string]),
              ]}
            />
            <Select
              label="Region"
              value={region}
              onChange={v => patch({ region: v })}
              options={[
                ['', 'All'],
                ['lon1', 'London'],
                ['fra1', 'Frankfurt'],
                ['nyc3', 'New York'],
              ]}
            />
            <Select
              label="Source"
              value={source}
              onChange={v => patch({ source: v })}
              options={[
                ['', 'All'],
                ['api', 'api'],
                ['auth', 'auth'],
                ['plugin', 'plugin'],
                ['cache', 'cache'],
                ['installer', 'installer'],
                ['provisioner', 'provisioner'],
                ['webhook', 'webhook'],
                ['billing', 'billing'],
                ['cron', 'cron'],
              ]}
            />

            <div className="flex items-center gap-1">
              {(['error', 'warn', 'info', 'debug'] as LogLevel[]).map(l => (
                <Tooltip key={l} content={`Toggle ${l.toUpperCase()} log lines`}>
                  <button
                    onClick={() => toggleLevel(l)}
                    className={cn(
                      'h-6 px-2 text-2xs font-semibold uppercase rounded border transition-colors',
                      levels.has(l)
                        ? 'bg-surface-2 border-border-strong text-fg'
                        : 'border-transparent text-fg-subtle hover:bg-surface-2/50',
                    )}
                  >
                    {l}
                  </button>
                </Tooltip>
              ))}
            </div>

            <select
              value={range}
              onChange={e => patch({ range: e.target.value, live: '' })}
              className="h-7 px-2 rounded-md bg-surface-2 border border-transparent text-sm hover:border-border focus:border-accent focus:outline-none"
            >
              {TIME_RANGES.map(r => (
                <option key={r.key} value={r.key}>{r.label}</option>
              ))}
            </select>

            <Tooltip content={live ? 'Pause live tail' : 'Stream new log lines as they arrive'}>
              <Button
                variant={live ? 'primary' : 'secondary'}
                size="sm"
                iconLeft={live ? <Pause size={12} /> : <Play size={12} />}
                onClick={() => patch({ live: live ? '' : '1' })}
              >
                {live ? 'Live tail · on' : 'Live tail'}
              </Button>
            </Tooltip>

            <div className="ml-auto inline-flex items-center gap-1 text-2xs text-fg-subtle">
              <Filter size={11} /> {filtered.length} of {allLogs.length}
            </div>
          </div>

          {/* Active filter chips */}
          {activeChips.length > 0 && (
            <div className="px-3 py-2 border-b border-border flex items-center gap-1.5 flex-wrap">
              {activeChips.map(c => (
                <span key={c.key} className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[color:var(--accent-soft)] text-accent text-2xs font-semibold">
                  {c.label}
                  <button onClick={c.clear} className="hover:opacity-70" aria-label={`Clear ${c.key}`}>
                    <X size={11} />
                  </button>
                </span>
              ))}
              <button onClick={clearAll} className="text-2xs text-fg-muted hover:text-fg ml-1">
                Clear all
              </button>
            </div>
          )}

          {/* Log stream */}
          <div className="bg-bg-elevated font-mono text-xs leading-relaxed py-2 max-h-[70vh] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-16 text-center font-sans">
                <div className="text-sm font-semibold text-fg mb-1">No logs to show</div>
                <p className="text-xs text-fg-muted max-w-lg mx-auto leading-relaxed">
                  Log ingestion isn't wired up yet — each NousViz workspace needs to
                  expose <code className="font-mono">/admin/v1/logs</code> or ship to a
                  central collector (Loki / Vector). Until then this page stays empty
                  rather than fake.
                </p>
                {activeChips.length > 0 && (
                  <button onClick={clearAll} className="text-accent hover:underline text-xs mt-3 inline-block">
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              filtered.map(line => (
                <div
                  key={line.id}
                  className="px-3 py-0.5 hover:bg-surface-2/50 flex items-baseline gap-2.5 group"
                >
                  <span className="text-fg-subtle shrink-0">{line.ts.slice(11, 19)}</span>
                  <Badge tone={LEVEL_TONE[line.level]} className="shrink-0 uppercase">
                    {line.level}
                  </Badge>
                  <FilterChip label={line.source} onClick={() => patch({ source: line.source })} />
                  <FilterChip label={REGION_LABEL[line.region] ?? line.region} onClick={() => patch({ region: line.region })} />
                  <Link
                    to={`/tenants/${line.tenantId}`}
                    onClick={e => { e.preventDefault(); patch({ tenant: line.tenantId }); }}
                    className="shrink-0 px-1.5 h-4 rounded text-2xs font-mono bg-surface-2 text-fg-muted hover:text-accent hover:bg-[color:var(--accent-soft)]"
                    title={`Filter to ${line.tenantName}`}
                  >
                    {line.tenantName}
                  </Link>
                  <button
                    onClick={() => patch({ workspace: line.workspaceId })}
                    className="shrink-0 px-1.5 h-4 rounded text-2xs font-mono bg-surface-2/60 text-fg-subtle hover:text-accent hover:bg-[color:var(--accent-soft)]"
                    title={`Filter to workspace ${line.workspaceName}`}
                  >
                    {line.workspaceId}
                  </button>
                  <span className="text-fg break-all flex-1 min-w-0">{line.msg}</span>
                </div>
              ))
            )}
          </div>

          <div className="px-3 py-2 border-t border-border text-2xs text-fg-subtle flex items-center justify-between flex-wrap gap-2">
            <span>
              <kbd className="font-mono bg-surface-2 border border-border rounded px-1">/</kbd> focus search ·
              click any chip to filter · <kbd className="font-mono bg-surface-2 border border-border rounded px-1">L</kbd> toggle live tail
            </span>
            <span className="tabnum">
              {live && <span className="text-accent mr-2 inline-flex items-center gap-1">
                <span className="inline-flex w-1.5 h-1.5 rounded-full bg-accent animate-pulse-dot" /> streaming
              </span>}
              {range} window
            </span>
          </div>
        </Card>
      </PageBody>
    </>
  );
}

function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      <span className="text-2xs text-fg-subtle uppercase tracking-wider font-semibold">{label}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="h-7 px-2 rounded-md bg-surface-2 border border-transparent text-sm hover:border-border focus:border-accent focus:outline-none max-w-[180px]"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    </div>
  );
}

function FilterChip({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="shrink-0 px-1.5 h-4 rounded text-2xs font-mono bg-surface-2/60 text-fg-subtle hover:text-accent hover:bg-[color:var(--accent-soft)]"
      title={`Filter to ${label}`}
    >
      {label}
    </button>
  );
}
